import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { uploadVideoInParts, uploadErrorMessage } from '@/lib/upload';

const uuid = () => crypto.randomUUID().replaceAll('-', '');
const blank = () => ({ assetIds: [], brief: '', targetSeconds: 30, requestId: uuid() });
export const studioError = (error) => typeof error.response?.data?.detail === 'string' ? error.response.data.detail : error.response?.status === 401 ? 'Please sign in again to access Creator Studio.' : 'The request could not finish. Saved assets and edits are unchanged. Please retry.';

export function useCreatorStudio(userId) {
  const key = `cg_studio_draft:${userId}`;
  const [draft, setDraft] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(key));
      if (saved && Array.isArray(saved.assetIds) && saved.assetIds.length <= 20 && typeof saved.brief === 'string' && [15, 30, 60].includes(saved.targetSeconds) && /^[a-f0-9]{32}$/.test(saved.requestId)) return saved;
    } catch {}
    return blank();
  });
  const [assets, setAssets] = useState([]), [jobs, setJobs] = useState([]), [job, setJob] = useState(null);
  const [uploads, setUploads] = useState([]), [busy, setBusy] = useState('loading'), [error, setError] = useState('');
  const alive = useRef(false), operation = useRef(false), controller = useRef(null), queue = useRef([]);
  const draftRef = useRef(draft), selectedJob = useRef(null);
  draftRef.current = draft;
  useEffect(() => { sessionStorage.setItem(key, JSON.stringify(draft)); }, [key, draft]);
  const refresh = useCallback(async () => {
    const [a, j] = await Promise.all([api.get('/studio/assets'), api.get('/studio/jobs')]);
    if (!alive.current) return;
    setAssets(a.data); setJobs(j.data);
    if (selectedJob.current) setJob(j.data.find(item => item.id === selectedJob.current) || null);
    else if (j.data.some(item => item.active || !['completed', 'failed'].includes(item.status))) {
      const active = j.data.find(item => item.active || !['completed', 'failed'].includes(item.status));
      selectedJob.current = active.id; setJob(active);
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    refresh().catch(e => { if (alive.current) setError(studioError(e)); }).finally(() => { if (alive.current) setBusy(''); });
    return () => { alive.current = false; controller.current?.abort(); };
  }, [refresh]);
  const processing = assets.some(item => !['ready', 'failed'].includes(item.status)) || jobs.some(item => item.active || !['completed', 'failed'].includes(item.status));
  useEffect(() => {
    if (!processing) return;
    let cancelled = false, timer;
    const poll = async () => {
      try { await refresh(); } catch (e) { if (!cancelled && alive.current) setError(studioError(e)); }
      if (!cancelled) timer = setTimeout(poll, 2500);
    };
    timer = setTimeout(poll, 1800);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [processing, refresh]);
  const updateDraft = (update) => { setDraft(old => ({ ...old, ...update, requestId: uuid() })); setError(''); };
  const toggle = (id) => {
    const selected = draftRef.current.assetIds;
    if (!selected.includes(id) && selected.length >= 20) { setError('Choose at most 20 assets per montage.'); return; }
    updateDraft({ assetIds: selected.includes(id) ? selected.filter(item => item !== id) : [...selected, id] });
  };
  const perform = async (label, action) => {
    if (operation.current) return null;
    operation.current = true; setBusy(label); setError('');
    try { return await action(); }
    catch (e) { if (alive.current) setError(studioError(e)); return null; }
    finally { operation.current = false; if (alive.current) setBusy(''); }
  };
  const syncUploads = () => { if (alive.current) setUploads(queue.current.map(({ file, ...item }) => ({ ...item, filename: file.name }))); };
  const uploadFiles = async (files) => {
    if (operation.current) return;
    if (files) {
      const chosen = Array.from(files);
      if (!chosen.length) return;
      if (chosen.length > 20) { setError('Upload up to 20 files at once.'); return; }
      if (queue.current.some(item => item.status !== 'saved')) { setError('Resume or discard your pending upload queue before selecting more files.'); return; }
      for (const file of chosen) {
        const image = /\.(jpg|jpeg|png|webp)$/i.test(file.name);
        if ((!image && !/\.(mp4|mov|m4v|webm)$/i.test(file.name)) || !file.size || file.size > (image ? 12 : 100) * 1024 * 1024) {
          setError('Choose MP4/MOV/M4V/WebM up to 100 MB, or JPG/PNG/WebP photos up to 12 MB.'); return;
        }
      }
      queue.current = chosen.map(file => ({ id: uuid(), file, status: 'waiting', progress: 0, phase: 'Waiting to upload' }));
    }
    controller.current = new AbortController();
    await perform('upload', async () => {
      for (const item of queue.current) {
        if (item.status === 'saved') continue;
        item.status = 'uploading'; syncUploads();
        try {
          await uploadVideoInParts(item.file, { purpose: 'studio', trial_id: '', clips_requested: 1, caption_enabled: false, face_track: false }, { id: item.id },
            progress => { Object.assign(item, progress); syncUploads(); }, { deferProcessing: true, signal: controller.current.signal });
          if (controller.current.signal.aborted) { item.status = 'paused'; syncUploads(); break; }
          const { data } = await api.post('/studio/assets', { upload_id: item.id });
          item.status = 'saved'; item.phase = 'Saved · preparing preview'; item.progress = 100; syncUploads();
          if (alive.current) {
            setAssets(old => [data, ...old.filter(asset => asset.id !== data.id)]);
            setDraft(old => ({ ...old, assetIds: old.assetIds.includes(data.id) || old.assetIds.length >= 20 ? old.assetIds : [...old.assetIds, data.id], requestId: uuid() }));
          }
        } catch (e) {
          item.status = e.code === 'ERR_CANCELED' ? 'paused' : 'failed'; item.phase = e.code === 'ERR_CANCELED' ? 'Paused · saved parts kept' : uploadErrorMessage(e);
          syncUploads(); if (alive.current) setError(item.phase); break;
        }
      }
      await refresh();
    });
  };
  const selectJob = (id) => perform('history', async () => {
    const { data } = await api.get(`/studio/jobs/${id}`); if (alive.current) { selectedJob.current = id; setJob(data); } return data;
  });
  const generate = (retry = false) => perform('generate', async () => {
    const body = retry ? { request_id: job.id, asset_ids: job.asset_ids, target_seconds: job.target_seconds, brief: job.brief, confirmed: true }
      : { request_id: draft.requestId, asset_ids: draft.assetIds, target_seconds: draft.targetSeconds, brief: draft.brief, confirmed: true };
    const { data } = await api.post(retry ? `/studio/jobs/${job.id}/retry` : '/studio/jobs', body, { timeout: 30000 });
    if (alive.current) { selectedJob.current = data.id; setJob(data); await refresh(); } return data;
  });
  const applyAdjustments = (body) => perform('adjust', async () => {
    const { data } = await api.post(`/studio/jobs/${job.id}/adjustments`, body, { timeout: 30000 });
    if (alive.current) { setJob(data); await refresh(); } return data;
  });
  const retryAdjustments = () => perform('adjust', async () => {
    const { data } = await api.post(`/studio/jobs/${job.id}/adjustments/${job.edit.request_id}/retry`);
    if (alive.current) { setJob(data); await refresh(); } return data;
  });
  const prepareAgain = (id) => perform('prepare', async () => { await api.post(`/studio/assets/${id}/prepare`); await refresh(); });
  const discardUploads = () => { if (!operation.current) { queue.current = []; setUploads([]); setError(''); } };
  return { draft, assets, jobs, job, uploads, busy, error, refresh, updateDraft, toggle, uploadFiles, selectJob, generate, prepareAgain, applyAdjustments, retryAdjustments,
    pause: () => controller.current?.abort(), discardUploads, clearError: () => setError(''),
    retryUpload: () => uploadFiles(null), canResume: uploads.some(item => ['paused', 'failed', 'waiting'].includes(item.status)) };
}
